<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
        btnTitle="Add New"
        :showBtn="false"
        :showAddNew="false"
        title="Reward Points Approval"
      />
    </div>

    <div class="my-2 px-2">
      <DateQuerySetter
        btnTitle="Go"
        @onClickGo="onClickGo"
      >
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Customer </label>
          <AsyncSelect
            placeholder="Party Name, ID, Mobile No"
            :api-service="fetchContactProfiles"
            :reduce="(name) => name.id"
            v-model="contactProfileId"
            :additional-query="{type: 'customer'}"
            :format-label="formatPatientLabel"
          />
        </div>
      </DateQuerySetter>
    </div>

    <div class="d-flex justify-content-end m-2 mb-0">
        <button
            :disabled="disableApprovalBtn || loading"
            class="btn btn-success"
            @click="handleRewardStatusUpdate"
        >
           {{ loading ? "Approving... " : 'Approve Selected'}}
        </button>
    </div>

    <div class="col-12 px-2 mt-2">
      <div class="card card-height">
        <div class="table-container">
          <table class="table create-billing table-width table-bordered">
            <thead>
            <tr>
              <th>
                <input
                    type="checkbox"
                    id="selectAll"
                    v-model="selectAll"
                > <label for="selectAll">Select</label>
              </th>
              <th>Date</th>
              <th>Customer Name & ID</th>
              <th>Mobile No</th>
              <th class="text-right">Point Added</th>
              <th>Note</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, index) in rewardList" :key="index">
              <td>
                <input
                    type="checkbox"
                    v-model="item.selected"
                >
              </td>
              <td>{{ item.date }}</td>
              <td>{{ item.name }}</td>
              <td>{{ item.mobile }}</td>
              <td class="text-right">{{ item.points }}</td>
              <td>{{ item.note }}</td>
              <td>{{ item.status }}</td>
              <td>
                <button class="btn btn-sm btn-primary me-1"
                        @click="handleEdit(item.id)"
                >
                  <i class="fa fa-eye"></i>
                </button>
                <button
                    v-if="item.status === 'pending'"
                    class="btn btn-sm btn-danger"
                    :disabled="loading"
                    @click="handleCancel(item.id)"
                >
                  <i class="fa fa-times"></i>
                </button>
              </td>
            </tr>
            </tbody>

          </table>
          <p v-if="! rewardList.length" class="text-center mt-5">No data</p>
        </div>
      </div>
    </div>
    <div class="px-2"><Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/></div>
  </div>

    <div class="col-lg-8">
      <AddModal
        v-if="store.state.isModalOpenTwo"
        :rewardData="editRewardData"
        :rewardId="editRewardData.id || null"
        :rewardPointAcc="rewardPointAcc"
        @onSubmit="onClickGo"
      />
    </div>
    <Loader v-if="loader"/>

</template>

<script setup>
import DateQuerySetter from '@/components/atom/DateQuerySetter';
import { ref, inject, onMounted, computed  } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import TitleButton from '@/components/atom/TitleButton';
import Pagination from '@/components/atom/Pagination';
import handleContact from "@/services/modules/contact";
import AddModal from '@/components/atom/super-shop/reward-point/Add.vue';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import { useAsyncDropdownHelper } from "@/services/utils/asyncDropdownHelper";
import handleInventory from "@/services/modules/inventory";
import handlePurchase from '@/services/modules/purchase'
import Loader from "@/components/atom/LoaderComponent";

const router = useRouter()
const store = useStore()
let offset = ref(20)
let contactProfileId = ref(null)
const route = useRoute()
const status = ref(null)
const rewardList = ref([]);
const editRewardData = ref({});
const rewardPointAcc = ref([]);
const statusList = [
  {id: 'added', text: 'Added'},
  {id: 'redeemed', text: 'Redeemed'},
  {id: 'expired', text: 'Expired'},
];
const loader = ref(false);
const loading = ref(false);

const { fetchContactProfiles } = handleContact();
const { formatPatientLabel } = useAsyncDropdownHelper();
const { fetchRewardPointList, fetchRewardPointById, bulkRewardPointApproval, updateRewardPointStatus } = handleInventory();
const { getAccountHeadBySlag } = handlePurchase();

const showSuccess = inject('showSuccess');
const showError = inject('showError');

const companyId = computed(() => {
  return route.params.companyId
})
const startDate = computed(() => {
  return route.query.start
})
const endDate = computed(() => {
  return route.query.end
})
const page = computed(() => {
  return route.query.page
})

const disableApprovalBtn = computed(() => {
    if (rewardList.value.length === 0) {
        return true
    }

    return rewardList.value.filter(reward => reward.selected === true).length === 0
})

const selectAll = computed({
    get: () => {
        return rewardList.value.length > 0 && rewardList.value.every(reward => reward.selected)
    },
    set: (value) => {
        rewardList.value.forEach(reward => {
            reward.selected = value
        })
    }
})

const getQuery = () => {
  let query = '?company_id=' + companyId.value + '&status=pending';
  if(contactProfileId.value) query += '&contact_profile_id='+contactProfileId.value;
  if(startDate.value) query += '&start_date='+startDate.value;
  if(endDate.value) query += '&end_date='+endDate.value;
  query += '&offset='+ offset.value;
  query += '&page='+ page.value;

  return query
}

const onClickGo = async() => {
  loader.value = true
  const query = getQuery();
  try{
    const res = await fetchRewardPointList(query);
    if(! res.status) {
      rewardList.value = [];
      setPagination({data: []});
      return;
    }
    rewardList.value = res.data.data;
    setPagination(res.data);
  } catch(err){
    console.log(err)
  } finally {
    loader.value = false;
  }
};

function setPagination(data) {
  if(!data.data.length) return
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
};

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  await router.push({path: route.path, query: routeQuery})
  await onClickGo()
};

const handleRewardStatusUpdate = async () => {
   loading.value = true;
    const rewardIds = rewardList.value
        .filter(reward => reward.selected === true)
        .map(reward => reward.id)

    if (!rewardIds.length) {
        showError('Please select at least one reward point')
        return
    }

    try {
        const res = await bulkRewardPointApproval({
            company_id: route.params.companyId,
            reward_ids: rewardIds,
            status: 'approved'
        })

        if (!res.status) {
            showError(res.message)
            return
        }

        showSuccess(res.message)
        onPageChange(1)
    } catch (err) {
        showError('Something went wrong please try again later')
    } finally {
       loading.value = false
    }
}

const handleCancel = async (id) => {
    if (! window.confirm('Are you sure you want to cancel this reward point?')) return

    loading.value = true
    try {
        const res = await updateRewardPointStatus(id, {
            company_id: route.params.companyId,
            status: 'cancelled'
        })

        if (! res.status) {
            showError(res.message)
            return
        }

        showSuccess(res.message)
        onPageChange(1)
    } catch (err) {
        showError('Something went wrong please try again later')
    } finally {
        loading.value = false
    }
}

const handleEdit = async (id) => {
  const companyQuery = `?company_id=${companyId.value}`;
  try {
      const res = await fetchRewardPointById(id, companyQuery);
      if(!res.status){
        showError(res.message);
        return;
      }
      editRewardData.value = res.data;
      store.state.isModalOpenTwo = true;
  } catch(err){
      showError("Error fetching reward point details");
  }
};

const getRewardPointAccounts = async () => {
  const query = `?company_id=${companyId.value}`

  try {
    const res = await getAccountHeadBySlag('reward_points', query);
    if(! res.status) {
      return;
    }
    rewardPointAcc.value = res.data;
  } catch (e) {
    showError("Something went wrong");
  }
}

onMounted( async() => {
  await onClickGo()
  await getRewardPointAccounts();
});
</script>
